import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { REVENUE_DATA, ELECTRICITY_PRICE } from '../constants';
import { Room, RoomStatus, Tenant } from '../types';
import Card from './ui/Card';

interface DashboardProps {
    rooms: Room[];
    tenants: Tenant[];
}

const Dashboard: React.FC<DashboardProps> = ({ rooms, tenants }) => {
    const totalRooms = rooms.length;
    const occupiedRooms = rooms.filter(room => room.status === RoomStatus.Occupied).length;
    const occupancyRate = totalRooms > 0 ? (occupiedRooms / totalRooms * 100).toFixed(1) : 0;
    
    const monthlyRevenue = rooms
        .filter(room => room.status === RoomStatus.Occupied)
        .reduce((sum, room) => {
            const electricityUsage = room.currentElectricityReading - room.lastElectricityReading;
            const electricityCost = electricityUsage > 0 ? electricityUsage * ELECTRICITY_PRICE : 0;
            return sum + room.price + electricityCost;
        }, 0);

    const upcomingExpirations = tenants.filter(tenant => {
        if (!tenant.contractEndDate) return false;
        const endDate = new Date(tenant.contractEndDate);
        const today = new Date();
        const diffTime = endDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays > 0 && diffDays <= 30;
    }).length;

    const roomStatusData = [
        { name: 'Còn trống', value: rooms.filter(r => r.status === RoomStatus.Available).length },
        { name: 'Đã thuê', value: occupiedRooms },
        { name: 'Bảo trì', value: rooms.filter(r => r.status === RoomStatus.Maintenance).length },
    ];
    const COLORS = ['#0088FE', '#00C49F', '#FFBB28'];

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card title="Tổng số phòng" value={totalRooms.toString()} />
                <Card title="Tỷ lệ lấp đầy" value={`${occupancyRate}%`} />
                <Card title="Doanh thu tháng (ước tính)" value={`${(monthlyRevenue / 1000000).toFixed(2)} Tr VNĐ`} />
                <Card title="Hợp đồng sắp hết hạn" value={upcomingExpirations.toString()} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white dark:bg-gray-900 p-6 rounded-xl shadow-lg">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Tổng quan doanh thu (Triệu VNĐ)</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={REVENUE_DATA} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.2)" />
                            <XAxis dataKey="name" stroke="rgb(156 163 175)" />
                            <YAxis stroke="rgb(156 163 175)" />
                            <Tooltip contentStyle={{ backgroundColor: 'rgba(31, 41, 55, 0.8)', border: 'none', color: '#fff' }} cursor={{fill: 'rgba(128,128,128,0.1)'}}/>
                            <Legend />
                            <Bar dataKey="revenue" fill="#3b82f6" name="Doanh thu (Tr VNĐ)"/>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-lg">
                     <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Tình trạng phòng</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={roomStatusData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                                nameKey="name"
                                label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
                            >
                                {roomStatusData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip contentStyle={{ backgroundColor: 'rgba(31, 41, 55, 0.8)', border: 'none' }}/>
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;